class MiniCart {
  constructor() {
    
    // Core elements
    this.body = document.body;
    this.drawer = document.getElementById('mini-cart-drawer');
    this.drawerContent = this.drawer?.querySelector('.mini-cart-content');
    this.cartItems = this.drawer?.querySelector('.cart-items');
    this.cartLinks = document.querySelectorAll('a[href="/cart"]');
    
    // State management
    this.isUpdating = false;
    this.changeQueue = new Set();
    this.changeDebounceTimer = null;
    this.quantityUpdateTimer = null;
    
    console.log('MiniCart initialized:', {
      hasDrawer: !!this.drawer,
      hasContent: !!this.drawerContent,
      hasItems: !!this.cartItems,
      cartLinkCount: this.cartLinks.length
    });

    if (this.drawer) {
      this.init();
    }
  }

  init() {
    this.bindEvents();
    console.log('MiniCart events bound');
  }

  bindEvents() {
    // Cart toggle events
    this.cartLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        this.openDrawer();
      });
    });

    // Drawer events
    if (this.drawerContent) {
      this.drawerContent.addEventListener('click', (e) => {
        const closeButton = e.target.closest('.close-button');
        if (closeButton) {
          e.preventDefault();
          e.stopPropagation();
          this.closeDrawer();
        } else {
          e.stopPropagation();
        }
      });
    }

    // Cart item events
    if (this.cartItems) {
      this.cartItems.addEventListener('click', this.handleCartItemClick.bind(this));
      this.cartItems.addEventListener('change', this.handleCartItemChange.bind(this));
    }

    // Global events
    document.addEventListener('click', this.handleOutsideClick.bind(this));
    
    console.log('All event listeners bound successfully');
  }

  handleCartItemClick(e) {
    const quantityButton = e.target.closest('.quantity__button');
    if (!quantityButton) return;

    const item = quantityButton.closest('.cart-item');
    if (!item) return;

    const input = item.querySelector('.quantity__input');
    if (!input) return;

    const currentValue = parseInt(input.value, 10);
    const isPlus = quantityButton.classList.contains('quantity__button--plus');
    const newValue = isPlus ? currentValue + 1 : Math.max(currentValue - 1, 0);

    console.log('Quantity change:', currentValue, 'to', newValue);

    // Update the input value immediately
    this.updateQuantityUI(item, newValue);

    // Clear any existing timer
    clearTimeout(this.quantityUpdateTimer);
    
    // Set new timer to update cart after delay
    this.quantityUpdateTimer = setTimeout(() => {
      this.queueQuantityChange(item, newValue);
    }, 500);
  }

  handleCartItemChange(e) {
    if (e.target.classList.contains('cart-item__subscription-checkbox') || 
        e.target.classList.contains('cart-item__subscription-schedule')) {
      this.handleSubscriptionChange(e);
      return;
    }

    if (!e.target.classList.contains('quantity__input')) return;

    const item = e.target.closest('.cart-item');
    if (!item) return;

    const newValue = Math.max(0, parseInt(e.target.value, 10) || 0);
    
    console.log('Input change:', e.target.value, 'to', newValue);

    this.updateQuantityUI(item, newValue);
    
    // Clear any existing timer
    clearTimeout(this.quantityUpdateTimer);
    
    // Set new timer to update cart after delay
    this.quantityUpdateTimer = setTimeout(() => {
      this.queueQuantityChange(item, newValue);
    }, 500);
  }

  handleSubscriptionChange(e) {
    console.log('Change event detected on:', e.target);
    
    const cartItem = e.target.closest('.cart-item');
    if (!cartItem) {
      console.error('Cart item not found');
      return;
    }

    const variantId = cartItem.dataset.variantId;
    const lineItem = cartItem.dataset.lineItem;
    const isSubscription = e.target.classList.contains('cart-item__subscription-checkbox') ? 
                          e.target.checked : true;
    let sellingPlanId = '';

    console.log('Change detected:', {
      isSubscription,
      variantId,
      lineItem
    });

    if (isSubscription) {
      const scheduleSelect = cartItem.querySelector('.cart-item__subscription-schedule');
      sellingPlanId = scheduleSelect ? scheduleSelect.value : cartItem.dataset.defaultSellingPlanId;
      console.log('Subscription selected:', sellingPlanId);
    }

    const quantityInput = cartItem.querySelector('.quantity__input');
    if (!quantityInput) {
      console.error('Quantity input not found');
      return;
    }

    const quantity = quantityInput.value;

    this.transitionOut();

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        line: lineItem,
        quantity: quantity,
        selling_plan: isSubscription ? sellingPlanId : null
      })
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(cart => {
      console.log('Cart updated successfully:', cart);
      this.refreshCart();
    })
    .catch(error => {
      console.error('Error changing subscription:', error);
      this.transitionIn();
    });
  }

  handleOutsideClick(e) {
    if (!this.body.classList.contains('cart-open')) return;
    
    const isOutsideClick = !this.drawer.contains(e.target) && 
                         !e.target.closest('a[href="/cart"]');
    
    if (isOutsideClick) {
      this.closeDrawer();
    }
  }

  queueQuantityChange(item, quantity) {
    const change = {
      line: parseInt(item.dataset.lineItem),
      quantity: quantity
    };

    this.changeQueue.add(JSON.stringify(change));
    this.scheduleChange();
    
    console.log('Change queued:', change);
  }

  scheduleChange() {
    clearTimeout(this.changeDebounceTimer);
    this.changeDebounceTimer = setTimeout(() => this.processChangeQueue(), 500);
  }

  async processChangeQueue() {
    if (this.isUpdating || this.changeQueue.size === 0) return;
    
    this.isUpdating = true;
    this.transitionOut();

    try {
      console.log('Processing change queue:', this.changeQueue);

      const changes = Array.from(this.changeQueue)
        .map(json => JSON.parse(json))
        .sort((a, b) => b.line - a.line); // Process higher line numbers first
      
      // Process each change sequentially
      for (const change of changes) {
        await this.changeCartItem(change);
      }
      
      try {
        await window.rewardAutomation?.updateRewardStatus();
      } catch (error) {
        console.error('Error updating rewards:', error);
      }
      
      await this.refreshCart();
      this.changeQueue.clear();
      
    } catch (error) {
      console.error('Error processing changes:', error);
      this.transitionIn();
    } finally {
      this.isUpdating = false;
    }
  }

  async changeCartItem(change) {
    console.log('Changing cart item:', change);

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(change)
      });

      if (!response.ok) {
        throw new Error('Cart change failed');
      }

      const result = await response.json();
      console.log('Cart change completed:', result);
      return result;
    } catch (error) {
      console.error('Failed to change cart:', error);
      throw error;
    }
  }

  async refreshCart() {
    try {
      console.log('Starting cart refresh');
      console.log('Current stylesheets before refresh:', Array.from(document.styleSheets).map(sheet => sheet.href));
      
      const [htmlResponse, cartData] = await Promise.all([
        fetch('/cart?view=raw').then(r => r.text()),
        fetch('/cart.js').then(r => r.json())
      ]);

      
      // Log before HTML update
      console.log('Before updateCartHTML');
      this.updateCartHTML(htmlResponse);
      console.log('After updateCartHTML');
      console.log('Current stylesheets after HTML update:', Array.from(document.styleSheets).map(sheet => sheet.href));

      this.updateCartCounts(cartData.item_count);
      
      const subtotalElement = document.querySelector('.subtotal');
      if (subtotalElement) {
        // Get currency from cart data or theme settings
        const currency = cartData.currency || 'USD';
        const formattedPrice = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: currency
        }).format(cartData.total_price / 100);
        
        subtotalElement.textContent = formattedPrice;
      }
      
      console.log('Cart refresh complete:', {
        itemCount: cartData.item_count,
        total: cartData.total_price
      });

    } catch (error) {
      console.error('Error refreshing cart:', error);
    } finally {
      this.transitionIn();
    }
  }

  updateCartHTML(html) {
    console.log('updateCartHTML called');
    if (!this.cartItems) {
      console.log('Creating new cart-items div');
      this.cartItems = document.createElement('div');
      this.cartItems.className = 'cart-items';
      this.drawerContent.appendChild(this.cartItems);
    }
    console.log('Setting innerHTML');
    this.cartItems.innerHTML = html;
    console.log('innerHTML set complete');
  }

  updateCartCounts(count) {
    document.querySelectorAll('[data-cart-count]')
      .forEach(el => el.setAttribute('data-cart-count', count));
  }

  updateQuantityUI(item, quantity) {
    const input = item.querySelector('.quantity__input');
    const minusButton = item.querySelector('.quantity__button--minus');
    const deleteButton = item.querySelector('.quantity__button--del');

    input.value = quantity;

    if (quantity > 1) {
      minusButton.style.display = 'flex';
      minusButton.disabled = false;
      deleteButton.style.display = 'none';
    } else {
      minusButton.style.display = 'none';
      minusButton.disabled = true;
      deleteButton.style.display = 'flex';
    }

    if (quantity === 0) {
      console.log('Item quantity is 0');
    }
  }

  openDrawer() {
    console.log('Opening cart drawer');
    this.body.classList.add('cart-open');
    this.drawer.setAttribute('aria-hidden', 'false');
  }

  closeDrawer() {
    console.log('Closing cart drawer');
    this.body.classList.remove('cart-open');
    this.drawer.setAttribute('aria-hidden', 'true');
  }

  transitionOut() {
    if (this.cartItems) {
      this.cartItems.style.opacity = '0.2';
    }
  }

  transitionIn() {
    if (this.cartItems) {
      this.cartItems.style.opacity = '1';
    }
  }
}

// Initialize MiniCart
let miniCart;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    console.log('Initializing MiniCart on DOMContentLoaded');
    miniCart = new MiniCart();
  });
} else {
  console.log('Initializing MiniCart immediately');
  miniCart = new MiniCart();
}

// Function to open cart from other pages/files
function openCart() {
  miniCart?.openDrawer();
}

// Handle delete button clicks
document.addEventListener('click', function(event) {
  const deleteButton = event.target.closest('.quantity__button--del');
  if (deleteButton) {
    event.preventDefault();
    console.log('Delete button clicked');
    const item = deleteButton.closest('.cart-item');
    if (item && miniCart) {
      miniCart.queueQuantityChange(item, 0);
    }
  }
}, true);

// Expose miniCart globally
window.miniCart = miniCart;